/**
 * IndexedDB Service - Main Entry Point
 * 
 * Provides a unified interface for all offline data operations,
 * initialization, and database management.
 */

import { offlineDB } from './database';
import {
  trainingSessionOps,
  techniqueOps,
  userTechniqueOps,
  positionOps,
  gamePlanOps,
  userProfileOps,
  roadmapMacroOps,
  roadmapMesoOps,
  permissionsOps,
  weeklyReportsOps,
  syncQueueOps,
  syncMetadataOps,
  staticAssetOps,
  referralCodeOps,
  referralOps,
  referralStatsOps
} from './operations';
import { STORES } from './schema';
import type {
  TrainingSessionOffline,
  TechniqueOffline,
  UserTechniqueOffline,
  PositionOffline,
  GamePlanOffline,
  UserProfileOffline,
  RoadmapMacroOffline,
  RoadmapMesoOffline,
  WeeklyReportOffline,
  SyncMetadata
} from './schema';

/**
 * Main IndexedDB Service Class
 */
export class IndexedDBService {
  private isInitialized = false;

  /**
   * Initialize the IndexedDB service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      await offlineDB.initialize();
      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Failed to initialize IndexedDB Service:', error);
      throw error;
    }
  }

  /**
   * Check if the service is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Training Sessions Operations
   */
  get trainingSessions() {
    return trainingSessionOps;
  }

  /**
   * Techniques Operations
   */
  get techniques() {
    return techniqueOps;
  }

  /**
   * User Techniques Operations
   */
  get userTechniques() {
    return userTechniqueOps;
  }

  /**
   * Positions Operations
   */
  get positions() {
    return positionOps;
  }

  /**
   * Game Plans Operations
   */
  get gamePlans() {
    return gamePlanOps;
  }

  /**
   * User Profile Operations
   */
  get userProfile() {
    return userProfileOps;
  }

  /**
   * Roadmap Macro Operations
   */
  get roadmapMacro() {
    return roadmapMacroOps;
  }

  /**
   * Roadmap Meso Operations
   */
  get roadmapMeso() {
    return roadmapMesoOps;
  }

  /**
   * Permissions Operations
   */
  get permissions() {
    return permissionsOps;
  }

  /**
   * Weekly Reports Operations
   */
  get weeklyReports() {
    return weeklyReportsOps;
  }

  /**
   * Sync Queue Operations
   */
  get syncQueue() {
    return syncQueueOps;
  }

  /**
   * Sync Metadata Operations
   */
  get syncMetadata() {
    return syncMetadataOps;
  }

  /**
   * Static Assets Operations
   */
  get staticAssets() {
    return staticAssetOps;
  }

  /**
   * Referral Code Operations
   */
  get referralCodes() {
    return referralCodeOps;
  }

  /**
   * Referral Operations
   */
  get referrals() {
    return referralOps;
  }

  /**
   * Referral Stats Operations
   */
  get referralStats() {
    return referralStatsOps;
  }

  /**
   * Direct access to offlineDB for advanced operations
   */
  get offlineDB() {
    return offlineDB;
  }

  /**
   * Database Statistics
   */
  async getStats(): Promise<{
    trainingSessions: number;
    techniques: number;
    userTechniques: number;
    positions: number;
    gamePlans: number;
    roadmapMacro: number;
    roadmapMeso: number;
    weeklyReports: number;
    syncQueueItems: number;
  }> {
    await this.initialize();

    const [
      trainingSessions,
      techniques,
      userTechniques,
      positions,
      gamePlans,
      roadmapMacro,
      roadmapMeso,
      weeklyReports,
      syncQueueItems
    ] = await Promise.all([
      offlineDB.count(STORES.TRAINING_SESSIONS),
      offlineDB.count(STORES.TECHNIQUES),
      offlineDB.count(STORES.USER_TECHNIQUES),
      offlineDB.count(STORES.POSITIONS),
      offlineDB.count(STORES.GAME_PLANS),
      offlineDB.count(STORES.ROADMAP_MACRO),
      offlineDB.count(STORES.ROADMAP_MESO),
      offlineDB.count(STORES.WEEKLY_REPORTS),
      offlineDB.count(STORES.SYNC_QUEUE)
    ]);

    return {
      trainingSessions,
      techniques,
      userTechniques,
      positions,
      gamePlans,
      roadmapMacro,
      roadmapMeso,
      weeklyReports,
      syncQueueItems
    };
  }

  /**
   * Clear all data (for testing or reset)
   */
  async clearAllData(): Promise<void> {
    await this.initialize();

    const stores = Object.values(STORES);
    await Promise.all(stores.map(store => offlineDB.clear(store)));
    

  }

  /**
   * Export data for backup
   */
  async exportData(): Promise<{
    trainingSessions: TrainingSessionOffline[];
    techniques: TechniqueOffline[];
    userTechniques: UserTechniqueOffline[];
    positions: PositionOffline[];
    gamePlans: GamePlanOffline[];
    userProfile: UserProfileOffline[];
    roadmapMacro: RoadmapMacroOffline[];
    roadmapMeso: RoadmapMesoOffline[];
    weeklyReports: WeeklyReportOffline[];
    syncMetadata: SyncMetadata[];
  }> {
    await this.initialize();

    const [
      trainingSessions,
      techniques,
      userTechniques,
      positions,
      gamePlans,
      userProfile,
      roadmapMacro,
      roadmapMeso,
      weeklyReports,
      syncMetadata
    ] = await Promise.all([
      offlineDB.getAll(STORES.TRAINING_SESSIONS),
      offlineDB.getAll(STORES.TECHNIQUES),
      offlineDB.getAll(STORES.USER_TECHNIQUES),
      offlineDB.getAll(STORES.POSITIONS),
      offlineDB.getAll(STORES.GAME_PLANS),
      offlineDB.getAll(STORES.USER_PROFILE),
      offlineDB.getAll(STORES.ROADMAP_MACRO),
      offlineDB.getAll(STORES.ROADMAP_MESO),
      offlineDB.getAll(STORES.WEEKLY_REPORTS),
      offlineDB.getAll(STORES.SYNC_METADATA)
    ]);

    return {
      trainingSessions: trainingSessions as TrainingSessionOffline[],
      techniques: techniques as TechniqueOffline[],
      userTechniques: userTechniques as UserTechniqueOffline[],
      positions: positions as PositionOffline[],
      gamePlans: gamePlans as GamePlanOffline[],
      userProfile: userProfile as UserProfileOffline[],
      roadmapMacro: roadmapMacro as RoadmapMacroOffline[],
      roadmapMeso: roadmapMeso as RoadmapMesoOffline[],
      weeklyReports: weeklyReports as WeeklyReportOffline[],
      syncMetadata: syncMetadata as SyncMetadata[]
    };
  }

  /**
   * Import data from backup
   */
  async importData(data: {
    trainingSessions?: TrainingSessionOffline[];
    techniques?: TechniqueOffline[];
    userTechniques?: UserTechniqueOffline[];
    positions?: PositionOffline[];
    gamePlans?: GamePlanOffline[];
    userProfile?: UserProfileOffline[];
    roadmapMacro?: RoadmapMacroOffline[];
    roadmapMeso?: RoadmapMesoOffline[];
    weeklyReports?: WeeklyReportOffline[];
    syncMetadata?: SyncMetadata[];
  }): Promise<void> {
    await this.initialize();

    const importPromises: Promise<void>[] = [];

    if (data.trainingSessions) {
      importPromises.push(offlineDB.putMany(STORES.TRAINING_SESSIONS, data.trainingSessions));
    }
    if (data.techniques) {
      importPromises.push(offlineDB.putMany(STORES.TECHNIQUES, data.techniques));
    }
    if (data.userTechniques) {
      importPromises.push(offlineDB.putMany(STORES.USER_TECHNIQUES, data.userTechniques));
    }
    if (data.positions) {
      importPromises.push(offlineDB.putMany(STORES.POSITIONS, data.positions));
    }
    if (data.gamePlans) {
      importPromises.push(offlineDB.putMany(STORES.GAME_PLANS, data.gamePlans));
    }
    if (data.userProfile) {
      importPromises.push(offlineDB.putMany(STORES.USER_PROFILE, data.userProfile));
    }
    if (data.roadmapMacro) {
      importPromises.push(offlineDB.putMany(STORES.ROADMAP_MACRO, data.roadmapMacro));
    }
    if (data.roadmapMeso) {
      importPromises.push(offlineDB.putMany(STORES.ROADMAP_MESO, data.roadmapMeso));
    }
    if (data.weeklyReports) {
      importPromises.push(offlineDB.putMany(STORES.WEEKLY_REPORTS, data.weeklyReports));
    }
    if (data.syncMetadata) {
      importPromises.push(offlineDB.putMany(STORES.SYNC_METADATA, data.syncMetadata));
    }

    await Promise.all(importPromises);

  }

  /**
   * Get storage usage information
   */
  async getStorageInfo(): Promise<{
    usage: number;
    quota: number;
    usagePercentage: number;
  }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const usage = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const usagePercentage = quota > 0 ? (usage / quota) * 100 : 0;

      return {
        usage,
        quota,
        usagePercentage
      };
    }

    return {
      usage: 0,
      quota: 0,
      usagePercentage: 0
    };
  }

  /**
   * Close the database connection
   */
  close(): void {
    offlineDB.close();
    this.isInitialized = false;
  }

  /**
   * Health check for the database
   */
  async healthCheck(): Promise<{
    status: 'healthy' | 'error';
    message: string;
    details?: Record<string, unknown>;
  }> {
    try {
      await this.initialize();
      const stats = await this.getStats();
      
      return {
        status: 'healthy',
        message: 'IndexedDB is working correctly',
        details: stats
      };
    } catch (error) {
      return {
        status: 'error',
        message: `IndexedDB health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        details: error instanceof Error ? { message: error.message, stack: error.stack } : { error: String(error) }
      };
    }
  }
}

// Export singleton instance
export const indexedDBService = new IndexedDBService();

// Export all operations for direct access if needed
export {
  trainingSessionOps,
  techniqueOps,
  userTechniqueOps,
  positionOps,
  gamePlanOps,
  userProfileOps,
  roadmapMacroOps,
  roadmapMesoOps,
  permissionsOps,
  weeklyReportsOps,
  syncQueueOps,
  syncMetadataOps,
  staticAssetOps,
  referralCodeOps,
  referralOps,
  referralStatsOps
};

// Export types
export type {
  TrainingSessionOffline,
  TechniqueOffline,
  UserTechniqueOffline,
  PositionOffline,
  GamePlanOffline,
  UserProfileOffline,
  RoadmapMacroOffline,
  RoadmapMesoOffline,
  PermissionsOffline,
  WeeklyReportOffline,
  StaticAssetOffline,
  ReferralCodeOffline,
  ReferralOffline,
  ReferralStatsOffline,
  SyncQueueItem,
  SyncMetadata
} from './schema';

// Export database instance for advanced usage
export { offlineDB } from './database';

// Note: syncService is exported from the main offline index.ts to avoid circular dependencies
